import { useCallback, useLayoutEffect, useRef } from 'react'
import clsx from 'clsx'

import { DateUtils } from 'src/utils/date'
import { FormatDateStringProps } from 'src/utils/date/types'
import { CURRENT_TZ } from 'src/hook/useSchedulePostForm'
import i18n from 'src/i18n'
import { Loading } from 'src/components'
import { useUser } from 'src/hook/useUser'

import { useSchedulePostsRequest } from '../SchedulePostModal/hooks/useSchedulePostsRequest'
import styles from './schedulePostList.module.css'

const SchedulePostList = () => {
  const { locale } = useUser()
  const ref = useRef<HTMLDivElement>(null)
  const {
    infinityReq: {
      data,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      isFetching,
      isLoading,
      error
    }
  } = useSchedulePostsRequest()

  const handleScroll = useCallback(
    async (e: any) => {
      const { scrollHeight, scrollTop, clientHeight } = e.target

      if (
        e?.target &&
        hasNextPage &&
        !isFetching &&
        scrollHeight - scrollTop <= clientHeight * 1.2
      ) {
        e.target.style.height = `${clientHeight + scrollTop}px`
        await fetchNextPage()
      }
    },
    [hasNextPage, isFetching]
  )

  useLayoutEffect(() => {
    ref?.current?.addEventListener('scroll', handleScroll)

    return () => {
      ref?.current?.removeEventListener('scroll', handleScroll)
    }
  }, [ref?.current, handleScroll])

  const getFormattedDate = useCallback((payload: FormatDateStringProps) => {
    const formattedDateAndTime = DateUtils.formatDateAndTime(payload)

    return i18n.t('modals.schedule_posts.buttons.schedule_info', formattedDateAndTime ?? undefined)
  }, [])

  if (isLoading && !error) {
    return <Loading full />
  }

  return data?.pages ? (
    <div ref={ref} className={clsx('_overflow-y-auto', styles.box)}>
      <ul className="_d-flex _flex-column _border _mb-0 _pl-0 _rounded">
        {data.pages.map((page) =>
          page.data.map((item, i) => (
            <li
              key={`${item.id}-${i}`}
              className={clsx(
                '_text-gray-700 _outline-0 _rounded-0 _m-0 _d-block _position-relative _px-5 _py-4',
                styles.listItem
              )}
            >
              <span className={clsx('_d-flex _font-weight-light', styles.itemTitle)}>
                {item.title}
              </span>
              <span>
                {`${item.authorName} — ${getFormattedDate({
                  dateString: new Date(item.scheduledPubDate as string).toString(),
                  timeZone: CURRENT_TZ,
                  locale
                })}`}
              </span>
            </li>
          ))
        )}
        {hasNextPage && isFetchingNextPage ? <Loading full /> : null}
      </ul>
    </div>
  ) : null
}

export default SchedulePostList
